import {
  isAuditType,
  isRatingLevel,
  v2ScopeToAuditScope,
  type Audit,
  type AuditSubject,
  type SubjectType,
} from "@/services/api/guardiansv2/types";
import type {
  V2AuditCompletedPayload,
  V2AuditQueuedPayload,
  V2AuditRunningPayload,
} from "@/context/MissionControlWebSocketContext";

/** WS payloads that share the queued/running audit envelope shape. */
export type V2AuditLifecyclePayload = V2AuditQueuedPayload | V2AuditRunningPayload;

const SUBJECT_TYPES: readonly SubjectType[] = [
  "interaction",
  "artifact",
  "mission",
  "task",
  "step",
  "trigger_event",
];

function isSubjectType(value: string): value is SubjectType {
  return (SUBJECT_TYPES as readonly string[]).includes(value);
}

export function subjectFromWsPayload(
  payload: V2AuditLifecyclePayload | V2AuditCompletedPayload
): AuditSubject {
  const raw = payload.subject;
  if (raw && isSubjectType(raw.type) && raw.id) {
    return {
      type: raw.type,
      id: raw.id,
      version: raw.version ?? null,
    };
  }
  const fallbackId = payload.scope.interaction_id ?? payload.audit_id;
  return { type: "interaction", id: fallbackId };
}

export function auditSkeletonFromWs(payload: V2AuditLifecyclePayload): Audit {
  return {
    _id: payload.audit_id,
    client_id: "",
    audit_type: isAuditType(payload.audit_type) ? payload.audit_type : "interaction",
    subject: subjectFromWsPayload(payload),
    scope: v2ScopeToAuditScope(payload.scope),
    inputs: { summary: "Evaluating…" },
    personas: {},
    personas_completed: [],
    rating: null,
    score: null,
    status: "queued",
    ticket_ids: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

export function appendQueuedAudit(audits: Audit[], payload: V2AuditQueuedPayload): Audit[] {
  if (audits.some((row) => row._id === payload.audit_id)) {
    return audits.map((audit) =>
      audit._id === payload.audit_id
        ? { ...auditSkeletonFromWs(payload), status: "queued" as const }
        : audit
    );
  }
  return [...audits, auditSkeletonFromWs(payload)];
}

export function applyAuditRunning(audits: Audit[], payload: V2AuditRunningPayload): Audit[] {
  if (audits.some((row) => row._id === payload.audit_id)) {
    return audits.map((audit) =>
      audit._id === payload.audit_id ? { ...audit, status: "running" as const } : audit
    );
  }
  return [...audits, { ...auditSkeletonFromWs(payload), status: "running" as const }];
}

export function applyAuditCompleted(audits: Audit[], payload: V2AuditCompletedPayload): Audit[] {
  return audits.map((audit) => {
    if (audit._id !== payload.audit_id) return audit;
    return {
      ...audit,
      subject: subjectFromWsPayload(payload),
      rating: isRatingLevel(payload.rating) ? payload.rating : audit.rating,
      score: payload.score ?? audit.score,
      status: "completed",
    };
  });
}

export function scopeMatchesPayload(
  scope: { missionId: string | null; taskId: string | null },
  payload: { scope: { mission_id?: string | null; task_id?: string | null } }
): boolean {
  if (scope.missionId && payload.scope.mission_id === scope.missionId) return true;
  if (scope.taskId && payload.scope.task_id === scope.taskId) return true;
  return false;
}
